import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import { buildPortfolio, DEMO_REPS as REPS, type PortfolioAccount } from "@/lib/extended";
import { ArrowUpRight, Quote } from "lucide-react";
import { getSession, getCurrentTenant } from "@/lib/session";
import { fetchRealPortfolio } from "@/pages/Pipeline";

const QUARTERS = ["all", "Q4-26", "Q1-27"] as const;
const CONFIDENCES = ["all", "High", "Medium", "Low"] as const;

export default function CompellingEvents() {
  const session = getSession();
  const tenant = getCurrentTenant();
  const isDemo = tenant?.is_demo === true;
  const tenantId = session?.tenantId ?? 1;

  const [portfolio, setPortfolio] = useState<PortfolioAccount[]>([]);
  const [q, setQ] = useState<(typeof QUARTERS)[number]>("all");
  const [owner, setOwner] = useState<string>("all");
  const [conf, setConf] = useState<(typeof CONFIDENCES)[number]>("all");

  useEffect(() => {
    if (isDemo) {
      setPortfolio(buildPortfolio());
    } else {
      fetchRealPortfolio(tenantId)
        .then((p) => setPortfolio(p))
        .catch(() => setPortfolio([]));
    }
  }, [isDemo, tenantId]);

  const ces = useMemo(() => {
    return portfolio
      .filter((a) => a.hasCompellingEvent && a.compellingEvent && a.ceDecisionDate)
      .map((a) => ({
        accountId: a.id,
        account: a.company,
        event: a.compellingEvent!,
        owner: a.rep,
        decisionDate: a.ceDecisionDate!,
        confidence: a.ceConfidence ?? "Medium",
        amount: a.amount,
        closeQuarter: a.closeQuarter,
        daysOut: daysBetween(new Date(), new Date(a.ceDecisionDate!)),
      }))
      .sort((a, b) => a.daysOut - b.daysOut);
  }, [portfolio]);

  const filtered = useMemo(
    () =>
      ces.filter(
        (c) =>
          (q === "all" || c.closeQuarter === q) &&
          (owner === "all" || c.owner === owner) &&
          (conf === "all" || c.confidence === conf),
      ),
    [ces, q, owner, conf],
  );

  return (
    <OperatorShell breadcrumbs={[{ label: "Firm Timelines" }]} pageTitle="Firm Timelines" pageMeta="Firm / formal timelines · portfolio">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">
          Firm / formal timelines tracker
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Every deal needs an official date
        </h1>
      </div>

      {/* Bryan ism banner */}
      <div className="card-shell-2 p-4 mb-6 flex items-start gap-3 border-l-2 border-[hsl(var(--accent-red))]">
        <Quote className="w-4 h-4 text-[hsl(var(--accent-red))] mt-0.5 shrink-0" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            From The Boss
          </div>
          <div className="text-[15px] mt-0.5 italic">
            You can&apos;t manufacture a formal timeline. Look for a firm or formal date the buyer has set.
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Filter
          label="Quarter"
          options={QUARTERS as unknown as string[]}
          value={q}
          onChange={(v) => setQ(v as (typeof QUARTERS)[number])}
        />
        <Filter
          label="Owner"
          options={isDemo ? ["all", ...REPS] as string[] : ["all"]}
          value={owner}
          onChange={setOwner}
        />
        <Filter
          label="Confidence"
          options={CONFIDENCES as unknown as string[]}
          value={conf}
          onChange={(v) => setConf(v as (typeof CONFIDENCES)[number])}
        />
      </div>

      <div className="card-shell overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                <th className="text-left px-4 py-3 font-medium">Account</th>
                <th className="text-left px-4 py-3 font-medium">Event</th>
                <th className="text-left px-4 py-3 font-medium">Owner</th>
                <th className="text-left px-4 py-3 font-medium">Decision</th>
                <th className="text-right px-4 py-3 font-medium">Days out</th>
                <th className="text-left px-4 py-3 font-medium">Confidence</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.accountId + c.event} className="border-b border-border last:border-0 hover:bg-[hsl(var(--surface-2))] transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground">{c.account}</div>
                    <div className="text-[11px] text-muted-foreground">{c.closeQuarter}</div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{c.event}</td>
                  <td className="px-4 py-3 text-muted-foreground">{c.owner}</td>
                  <td className="px-4 py-3 tabular text-muted-foreground">{c.decisionDate}</td>
                  <td className="px-4 py-3 text-right tabular">
                    <span className={c.daysOut <= 14 ? "text-rose-300" : c.daysOut <= 30 ? "text-orange-300" : "text-muted-foreground"}>
                      {c.daysOut}d
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <ConfidencePill v={c.confidence} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      href={`/account/${c.accountId}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 min-h-[36px] rounded border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))] hover:border-[hsl(var(--accent-red))]/40 transition-colors"
                    >
                      Open <ArrowUpRight className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-xs text-muted-foreground">
                    No firm or formal timelines match this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </OperatorShell>
  );
}

function daysBetween(a: Date, b: Date) {
  const ms = b.getTime() - a.getTime();
  return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
}

function ConfidencePill({ v }: { v: "High" | "Medium" | "Low" }) {
  const cls =
    v === "High"
      ? "bg-emerald-500/10 text-emerald-300 ring-emerald-500/30"
      : v === "Medium"
      ? "bg-orange-500/10 text-orange-300 ring-orange-500/30"
      : "bg-rose-500/10 text-rose-300 ring-rose-500/30";
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ring-1 ring-inset ${cls}`}>
      {v}
    </span>
  );
}

function Filter({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-xs">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-[hsl(var(--surface-2))] border border-border rounded px-2 py-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))]"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o === "all" ? "All" : o}
          </option>
        ))}
      </select>
    </label>
  );
}
